import type { Application } from '@data/hooks/api/use-applications-api/use-applications-api';

/**
 * Formats a grade string by stripping leading zeros.
 * e.g. "01" → "1", "00" → "0"
 */
export function formatGrade(grade: string) {
  return grade.replace(/^0+/, '') || '0';
}

/**
 * Formats a grade range for display.
 * Returns a single grade when low === high, or a "low-high" range otherwise.
 * e.g. ("03", "03") → "3", ("01", "05") → "1-5"
 */
export function formatGradeRange(gradeLow: string, gradeHigh: string): string {
  return gradeLow === gradeHigh
    ? formatGrade(gradeLow)
    : `${formatGrade(gradeLow)}-${formatGrade(gradeHigh)}`;
}

/**
 * Sort position of a grade code: PK → -1, K (or "0"/"00") → 0, numeric grades
 * → their number. NaN for anything unrecognised.
 */
function gradeRank(grade: string): number {
  const code = grade.trim().toUpperCase();
  if (code === 'PK') return -1;
  if (code === 'K') return 0;
  return Number(formatGrade(code));
}

/** The grade with the lowest (or highest) rank, ignoring unrecognised codes. */
function pickGrade(
  grades: string[],
  compare: (a: number, b: number) => boolean
): string | undefined {
  return grades
    .filter((grade) => !Number.isNaN(gradeRank(grade)))
    .reduce<string | undefined>(
      (best, grade) =>
        best === undefined || compare(gradeRank(grade), gradeRank(best))
          ? grade
          : best,
      undefined
    );
}

/**
 * Derives the widest grade range across all products in an application
 * and returns a formatted display string (e.g. "2-5" or "3").
 * Returns undefined when no grade info is available.
 *
 * Products are not assumed to be sorted: the range runs from the lowest
 * `gradeLow` to the highest `gradeHigh` of any product, ordering PK < K < 1–12.
 */
export function getGradeDisplay(application: Application): string | undefined {
  const products = application.products ?? [];
  const lows = products
    .map((p) => p.gradeLow?.trim())
    .filter(Boolean) as string[];
  const highs = products
    .map((p) => p.gradeHigh?.trim())
    .filter(Boolean) as string[];

  const gradeLow = pickGrade(lows, (a, b) => a < b);
  const gradeHigh = pickGrade(highs, (a, b) => a > b);

  return gradeLow && gradeHigh
    ? formatGradeRange(gradeLow, gradeHigh)
    : undefined;
}
